// #3294 -- pure model behind the equitation sections page. No React, no
// network: a flat list of items, section bars and riders interleaved, exactly as
// the page draws it.
//
//   [rider, rider,        <- riders above the first bar: "Not in a section"
//    bar,   rider, rider, <- Section 1
//    bar,   rider]        <- Section 2
//
// A rider belongs to the bar above it. A section's number is its bar's place
// among the bars, so numbers follow list order with no bookkeeping. Order is
// display only (placing ignores it, #3292).
//
// Item ids: a rider's id is its rideId. A bar's id is `bar:<n>` (a counter the
// page owns), and POOL_ID names the pinned "Not in a section" header, which is
// a drop target only. Collapse state is a Set of bar ids the page keeps; it is
// view-only and never reaches the model's output.

export const POOL_ID = "pool";
const BAR_PREFIX = "bar:";

export const isBarId = (id) => typeof id === "string" && id.startsWith(BAR_PREFIX);
export const makeBarId = (n) => `${BAR_PREFIX}${n}`;

const bar = (id) => ({ kind: "bar", id });
const rider = (id) => ({ kind: "rider", id });

/** Scheduled time, then ride number: the same order the drawer's ride list uses. */
export function sortRidesInRideOrder(rides) {
  return [...(rides || [])].sort((a, b) => {
    const ta = a.scheduledTime ? Date.parse(a.scheduledTime) : NaN;
    const tb = b.scheduledTime ? Date.parse(b.scheduledTime) : NaN;
    if (!Number.isNaN(ta) && !Number.isNaN(tb) && ta !== tb) return ta - tb;
    return (a.rideNumber ?? Infinity) - (b.rideNumber ?? Infinity);
  });
}

const validSection = (n) => Number.isInteger(n) && n > 0;

/**
 * Saved rides -> the flat list. Unsectioned riders first (ride order), then each
 * saved section ascending: its bar, its riders by subsectionOrder (a rider with
 * no order after the ones that have one, in ride order).
 */
export function buildItems(rides, nextBarId) {
  const ordered = sortRidesInRideOrder(rides);
  const items = ordered.filter((r) => !validSection(r.subsection)).map((r) => rider(r.id));
  const numbers = [...new Set(ordered.filter((r) => validSection(r.subsection)).map((r) => r.subsection))].sort(
    (a, b) => a - b,
  );
  for (const n of numbers) {
    items.push(bar(nextBarId()));
    ordered
      .filter((r) => r.subsection === n)
      .map((r, i) => ({ r, i }))
      .sort((a, b) => {
        const ao = Number.isInteger(a.r.subsectionOrder) ? a.r.subsectionOrder : Infinity;
        const bo = Number.isInteger(b.r.subsectionOrder) ? b.r.subsectionOrder : Infinity;
        return ao === bo ? a.i - b.i : ao < bo ? -1 : 1;
      })
      .forEach(({ r }) => items.push(rider(r.id)));
  }
  return items;
}

/** The saved rows as a map, the baseline `diffRows` compares against. */
export function savedRowsFromRides(rides) {
  const out = {};
  for (const r of rides || []) {
    out[r.id] = validSection(r.subsection)
      ? { subsection: r.subsection, subsectionOrder: Number.isInteger(r.subsectionOrder) ? r.subsectionOrder : null }
      : { subsection: null, subsectionOrder: null };
  }
  return out;
}

/**
 * The page's view of the list: the pool and the sections, with each section's
 * display number (its bar's place among ALL bars, empty ones included).
 */
export function layout(items) {
  const pool = [];
  const sections = [];
  let current = null;
  for (const it of items) {
    if (it.kind === "bar") {
      current = { id: it.id, number: sections.length + 1, riderIds: [] };
      sections.push(current);
    } else if (current) {
      current.riderIds.push(it.id);
    } else {
      pool.push(it.id);
    }
  }
  return { pool, sections };
}

/**
 * What would be saved: { rideId: { subsection, subsectionOrder } }. A bar with
 * no riders is not saved, so the saved numbers are compacted 1..k over the
 * non-empty bars (the server compacts the same way).
 */
export function toRows(items) {
  const rows = {};
  let number = 0;
  let order = 0;
  let inSection = false;
  for (const it of items) {
    if (it.kind === "bar") {
      inSection = true;
      order = 0; // the number is claimed when the first rider lands under the bar
      continue;
    }
    if (!inSection) {
      rows[it.id] = { subsection: null, subsectionOrder: null };
      continue;
    }
    if (order === 0) number += 1;
    rows[it.id] = { subsection: number, subsectionOrder: order };
    order += 1;
  }
  return rows;
}

/** Rows that differ from the saved baseline, in the batch route's body shape. */
export function diffRows(saved, next) {
  const out = [];
  for (const [rideId, to] of Object.entries(next)) {
    const from = saved[rideId];
    if (!from || from.subsection !== to.subsection || from.subsectionOrder !== to.subsectionOrder) {
      out.push({ rideId, subsection: to.subsection, subsectionOrder: to.subsectionOrder });
    }
  }
  return out;
}

const indexOfId = (items, id) => items.findIndex((it) => it.id === id);

function arrayMove(list, from, to) {
  const next = [...list];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

/** Index just past a bar's last rider (the next bar boundary). */
function sectionEnd(items, barIndex) {
  let i = barIndex + 1;
  while (i < items.length && items[i].kind !== "bar") i += 1;
  return i;
}

/** True when the bar has no rider directly under it. */
function barIsEmpty(items, barIndex) {
  return sectionEnd(items, barIndex) === barIndex + 1;
}

/**
 * Drop a rider. `overId` is what it was dropped on: a rider, a bar, or POOL_ID.
 * Returns { items, openedBarId } where openedBarId is the collapsed bar the
 * rider was dropped on (the page opens it). A drop on a collapsed or empty bar
 * puts the rider FIRST in that section; a drop on a rider or an expanded bar
 * is an ordinary sortable move.
 */
export function moveRider(items, activeId, overId, collapsed = new Set()) {
  const from = indexOfId(items, activeId);
  if (from < 0 || activeId === overId) return { items, openedBarId: null };
  if (overId === POOL_ID) {
    return { items: arrayMove(items, from, 0), openedBarId: null };
  }
  const over = indexOfId(items, overId);
  if (over < 0) return { items, openedBarId: null };
  if (items[over].kind === "bar" && (collapsed.has(overId) || barIsEmpty(items, over))) {
    const without = items.filter((it) => it.id !== activeId);
    const at = indexOfId(without, overId) + 1;
    const next = [...without];
    next.splice(at, 0, items[from]);
    return { items: next, openedBarId: collapsed.has(overId) ? overId : null };
  }
  return { items: arrayMove(items, from, over), openedBarId: null };
}

/**
 * Drag a bar. An OPEN bar moves alone: it changes where its section starts and
 * the riders stay where they are. A CLOSED bar carries its hidden riders and
 * snaps to a section boundary, never into the middle of another section: moving
 * down it lands after the end of the section it was dropped on, moving up it
 * lands before that section's bar. Onto the "Not in a section" header, or onto
 * a rider in that group, it lands just after the unsectioned riders. An open bar
 * dropped down onto a collapsed bar goes to the end of that section.
 */
export function moveBar(items, activeId, overId, collapsed = new Set()) {
  const from = indexOfId(items, activeId);
  if (from < 0 || activeId === overId) return items;
  let over = overId === POOL_ID ? -1 : indexOfId(items, overId);
  if (overId !== POOL_ID && over < 0) return items;
  if (!collapsed.has(activeId)) {
    if (over > from && items[over].kind === "bar" && collapsed.has(overId)) {
      // Dropped down onto a collapsed bar: the end of that section, not the gap
      // between the bar and its hidden riders.
      const without = items.filter((it) => it.id !== activeId);
      const at = sectionEnd(without, indexOfId(without, overId));
      const next = [...without];
      next.splice(at, 0, items[from]);
      return next;
    }
    return arrayMove(items, from, over < 0 ? 0 : over);
  }
  const blockEnd = sectionEnd(items, from);
  const block = items.slice(from, blockEnd);
  const rest = [...items.slice(0, from), ...items.slice(blockEnd)];
  // Just after the unsectioned riders (the first bar), never above them: a
  // closed section must not swallow the pool.
  const firstBar = rest.findIndex((it) => it.kind === "bar");
  const afterPool = firstBar < 0 ? rest.length : firstBar;
  if (over < 0) return [...rest.slice(0, afterPool), ...block, ...rest.slice(afterPool)];
  const movingDown = over > from;
  // Where `over` sits once the block is out.
  const overRest = over > from ? over - block.length : over;
  let at;
  if (movingDown) {
    // The end of the section `over` belongs to (its own bar's block end).
    let barIdx = overRest;
    while (barIdx >= 0 && rest[barIdx].kind !== "bar") barIdx -= 1;
    at = barIdx < 0 ? afterPool : sectionEnd(rest, barIdx);
  } else {
    // The start of the section `over` belongs to.
    let barIdx = overRest;
    while (barIdx >= 0 && rest[barIdx].kind !== "bar") barIdx -= 1;
    at = barIdx < 0 ? afterPool : barIdx;
  }
  const next = [...rest];
  next.splice(at, 0, ...block);
  return next;
}

/** Delete a bar. Its riders stay put, so they join the section above (or the pool). */
export function removeBar(items, barId) {
  return items.filter((it) => it.id !== barId);
}

/** An empty bar at the end of the list. */
export function addBar(items, id) {
  return [...items, bar(id)];
}

/** Every rider back to "Not in a section", in ride order, no bars. */
export function clearAll(rides) {
  return sortRidesInRideOrder(rides).map((r) => rider(r.id));
}

/** All riders in ride order, in sections of `size`: 23 riders at 10 -> 10 / 10 / 3. */
export function splitIntoSections(rides, size, nextBarId) {
  const ordered = sortRidesInRideOrder(rides);
  const n = Math.max(1, Math.floor(size));
  const items = [];
  ordered.forEach((r, i) => {
    if (i % n === 0) items.push(bar(nextBarId()));
    items.push(rider(r.id));
  });
  return items;
}
